import React, { useEffect, useRef } from "react";

const c3 = window.c3;

/** Đếm instance để mỗi biểu đồ có class riêng (c3 vẽ chữ bằng SVG nên không style inline được) */
let demInstance = 0;

const MAU_CHU = "#2B335E"; // .color7

/**
 * Khung biểu đồ tròn (donut) dùng chung cho các màn báo cáo.
 *
 * Component chỉ lo phần vẽ: vành donut, nhãn trên cung, chữ ở tâm, trạng thái
 * đang tải / không có dữ liệu. Tiêu đề, chú giải và bảng số liệu do màn gọi tự
 * bố trí bên ngoài để mỗi báo cáo linh hoạt layout riêng.
 *
 * @param {Array}  duLieu        [{ ten, giaTri, mau }] - vẽ đúng theo thứ tự mảng
 * @param {bool}   coTheVe       Ghi đè điều kiện vẽ (mặc định: có ít nhất 1 giá trị > 0)
 * @param {func}   dinhDangNhan  (giaTri, tyLe, ten) => string - nhãn trên cung
 * @param {func}   dinhDangTooltip (giaTri, tyLe, ten) => string
 * @param {node}   noiDungTam    Nội dung HTML đặt giữa vòng donut
 * @param {node}   thongBaoTrong Hiển thị khi không vẽ được biểu đồ
 */
export const BieuDoDonut = ({
  duLieu,
  coTheVe,
  chieuCao = 300,
  dayVanh = 50,
  dem = 8,
  hienChuThich = false,
  dinhDangNhan,
  nguongNhan = 0.02,
  dinhDangTooltip,
  mauNhan = MAU_CHU,
  coChuNhan = 13,
  coChuChuThich = 12,
  quangTrangNhan = true,
  noiDungTam,
  loading,
  thongBaoTrong = "Không có dữ liệu",
}) => {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);
  const lopRef = useRef(null);
  if (!lopRef.current) {
    demInstance += 1;
    lopRef.current = "bieu-do-donut-" + demInstance;
  }
  const lop = lopRef.current;

  const danhSach = duLieu || [];
  const veDuoc =
    coTheVe === undefined
      ? danhSach.some((item) => (item.giaTri || 0) > 0)
      : coTheVe;

  // Chỉ vẽ lại khi số liệu thực sự đổi
  const chuKyDuLieu = danhSach
    .map((item) => `${item.ten}=${item.giaTri}=${item.mau}`)
    .join("|");

  const customStyles = `
    /* Nhãn nằm đè lên cung nên dùng chữ đậm kèm quầng trắng để đọc rõ trên mọi nền */
    .${lop} .c3-chart-arc text {
        fill: ${mauNhan};
        font-size: ${coChuNhan}px;
        font-weight: 600;
        ${
          quangTrangNhan
            ? `stroke: rgba(255, 255, 255, 0.9);
        stroke-width: 3px;
        stroke-linejoin: round;
        paint-order: stroke fill;`
            : ""
        }
    }
    .${lop} .c3-legend-item text { font-size: ${coChuChuThich}px; }
    /* c3 đặt tâm cung cao hơn tâm khung 5px (arcHeight = height - 10) nên phải bù lại */
    .${lop} .bd-tam {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        transform: translateY(-5px);
        pointer-events: none;
        z-index: 3;
    }
  `;

  useEffect(() => {
    if (!chartRef.current || !c3) return undefined;

    const huyChart = () => {
      if (chartInstance.current) {
        try {
          chartInstance.current.destroy();
        } catch (e) {
          console.error(e);
        }
        chartInstance.current = null;
      }
    };

    if (loading || !veDuoc) {
      huyChart();
      return undefined;
    }

    const mau = {};
    danhSach.forEach((item) => {
      if (item.mau) mau[item.ten] = item.mau;
    });

    chartInstance.current = c3.generate({
      bindto: chartRef.current,
      data: {
        columns: danhSach.map((item) => [item.ten, item.giaTri]),
        type: "donut",
        // Giữ nguyên thứ tự truyền vào để màn gọi tự quyết cung nào nằm đâu
        order: null,
        colors: mau,
      },
      donut: {
        title: "",
        width: dayVanh,
        label: {
          threshold: nguongNhan,
          format: (value, ratio, id) =>
            dinhDangNhan
              ? dinhDangNhan(value, ratio, id)
              : `${(ratio * 100).toFixed(1)}%`,
        },
      },
      legend: { show: hienChuThich, position: "bottom" },
      padding: { top: dem, right: dem, bottom: dem, left: dem },
      size: { height: chieuCao },
      tooltip: {
        format: {
          value: (value, ratio, id) =>
            dinhDangTooltip
              ? dinhDangTooltip(value, ratio, id)
              : `${value} (${(ratio * 100).toFixed(1)}%)`,
        },
      },
    });

    return huyChart;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, veDuoc, chuKyDuLieu, chieuCao, dayVanh]);

  return (
    <div
      className={`bieu-do-donut ${lop}`}
      style={{
        position: "relative",
        width: "100%",
        height: chieuCao,
        overflow: "hidden",
      }}
    >
      <style>{customStyles}</style>

      {loading && (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(255, 255, 255, 0.5)",
            zIndex: 4,
          }}
        >
          <div className="spinner-border text-primary" role="status"></div>
        </div>
      )}

      {!loading && !veDuoc && (
        <div
          className="d-flex justify-content-center align-items-center text-muted text-center px-3"
          style={{ height: "100%" }}
        >
          {thongBaoTrong}
        </div>
      )}

      <div
        ref={chartRef}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          opacity: loading ? 0.5 : 1,
          zIndex: 1,
        }}
      />

      {!loading && veDuoc && noiDungTam ? (
        <div className="bd-tam">{noiDungTam}</div>
      ) : null}
    </div>
  );
};

export default BieuDoDonut;
